import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { getLandscapeEntities } from 'SensitiveDataLandscape/landscapeEntitiesApi';
import { BRAND, STATUS, OPERATION, FONT_MONO } from 'Utils/styleTokens';
import { sortByEntityPriority } from 'Utils/entityPriority';
import AppContext from 'Utils/AppContext';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';
import categoryMap from 'SensitiveDataLandscape/DataSourceRiskProfile/RiskCategories/category_map.json';

// Tile layout constants. Collapsed shows a fixed count; the "More" button reveals the rest.
// Tiles are a uniform fixed width (no flex-grow) so every card is the same size.
const TILE_WIDTH = 250;
const TILE_GAP = 12;
const COLLAPSED_COUNT = 12;

// Entity → category via the risk-profile category map; the tile dot uses the
// category color so the tiles and the risk-summary bars speak the same palette.
const CATEGORY_COLORS = {
  personal: BRAND.blue,
  financial: STATUS.orange.text,
  // Credentials & secrets (2026-08-18): passwords, keys, tokens. Danger-red —
  // exposed credentials are the direct-compromise class.
  credentials: STATUS.danger.text,
  healthcare: BRAND.violet,
  PHI: BRAND.cyan,
};

const entityCategory = (entityType) => {
  for (const [category, entities] of Object.entries(categoryMap)) {
    if (Array.isArray(entities) && entities.includes(entityType)) return category;
  }
  return 'personal';
};

const entityLabel = (entityType) =>
  String(entityType || '')
    .split('_')
    .map((w) => (w.length > 3 ? w.charAt(0) + w.slice(1).toLowerCase() : w))
    .join(' ');

const formatCount = (n) => Number(n || 0).toLocaleString();

// Whole-source protection pill: masked wins over encrypted for display; anything
// neither masked nor encrypted renders as UNPROTECTED.
const protectionOf = (source) => {
  if (source.maskStatus === 'MASKED') return { label: 'MASKED', ...OPERATION.mask };
  if (source.maskStatus === 'MASKING') return { label: 'MASKING…', color: STATUS.info.text, bg: STATUS.info.bg };
  if (source.encryptStatus === 'ENCRYPTED') return { label: 'ENCRYPTED', ...OPERATION.encrypt };
  if (source.maskStatus === 'ERROR' || source.encryptStatus === 'ERROR') {
    return { label: 'ERROR', color: STATUS.danger.text, bg: STATUS.danger.bg };
  }
  return { label: 'UNPROTECTED', color: STATUS.danger.text, bg: STATUS.danger.bg };
};

const isProtected = (source) => source.maskStatus === 'MASKED' || source.encryptStatus === 'ENCRYPTED';

function ProtectionPill({ source }) {
  const p = protectionOf(source);
  return (
    <span
      className='sg-pill sg-pill--nodot'
      style={{ background: p.bg, color: p.color }}
    >
      {p.label}
    </span>
  );
}

/**
 * "Entity types found" — mockup 2a. One card per entity type with the cross-source
 * instance total (the scatter-plot metric); clicking a card expands the source list
 * with per-source counts and whole-source mask/encrypt pills.
 * Calls onLoaded(tiles) so the pipeline board can reuse the per-source statuses.
 */
function EntityTiles({ onLoaded }) {
  const { entityOrder, connections } = useContext(AppContext) || {};
  const [tiles, setTiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Collapsed by default: show the first COLLAPSED_COUNT tiles, then a "More" button reveals the rest.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await getLandscapeEntities();
        if (cancelled) return;
        setTiles(result);
        if (onLoaded) onLoaded(result);
      } catch (e) {
        console.log('Failed to load /api/v1/landscape/entities', e);
        if (!cancelled) setError('Entity roll-up is unavailable. Run a discovery to populate it.');
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return null;
  if (error || tiles.length === 0) {
    return (
      <Card className='mb-3'>
        <Card.Body>
          <h5 className='text-left sg-eyebrow'>Entity types found</h5>
          <div className='sg-small' style={{ color: 'var(--ink-500)' }}>
            {error || 'No entities discovered yet. Run a discovery to populate this view.'}
          </div>
        </Card.Body>
      </Card>
    );
  }

  const selectedTile = tiles.find((t) => t.entityType === selected) || null;

  // Most important PII first. Operator override (Advanced Config → entityOrder) wins;
  // otherwise the shipped default in Utils/entityPriority.js. Stable sort keeps the
  // backend's total-desc order among entities of equal priority.
  const orderedTiles = sortByEntityPriority(tiles, (t) => t.entityType, entityOrder);
  // Show a fixed number of tiles when collapsed; the rest hide behind the "More" button.
  const collapsedCount = COLLAPSED_COUNT;
  const hasOverflow = orderedTiles.length > collapsedCount;
  const visibleTiles = expanded || !hasOverflow ? orderedTiles : orderedTiles.slice(0, collapsedCount);

  return (
    <Card className='mb-3'>
      <Card.Body>
        <h5 className='text-left sg-eyebrow'>
          Entity types found <span style={{ color: 'var(--ink-400)', fontWeight: 400 }}>— click to expand</span>
        </h5>
        <div className='d-flex flex-wrap' style={{ gap: TILE_GAP }}>
          {visibleTiles.map((tile) => {
            const category = entityCategory(tile.entityType);
            const unprotectedCount = tile.sources.filter((s) => !isProtected(s)).length;
            const isSelected = selected === tile.entityType;
            return (
              <button
                key={tile.entityType}
                type='button'
                data-testid={`entity-tile-${tile.entityType}`}
                onClick={() => setSelected(isSelected ? null : tile.entityType)}
                style={{
                  width: TILE_WIDTH,
                  flex: `0 0 ${TILE_WIDTH}px`,
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderRadius: 'var(--r-md)',
                  border: `1px solid ${isSelected ? BRAND.blue : 'var(--ink-150)'}`,
                  background: isSelected ? STATUS.info.bg : 'var(--surface)',
                  cursor: 'pointer',
                }}
              >
                <div className='d-flex align-items-center' style={{ gap: 7 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: CATEGORY_COLORS[category] || BRAND.blue,
                      flex: 'none',
                    }}
                  />
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-900)' }}>
                    {entityLabel(tile.entityType)}
                  </span>
                </div>
                <div style={{ fontFamily: FONT_MONO, fontSize: 18, fontWeight: 600, margin: '4px 0 2px' }}>
                  {formatCount(tile.total)}
                </div>
                <div
                  className='sg-small'
                  style={{ color: unprotectedCount > 0 ? STATUS.danger.text : STATUS.success.text }}
                >
                  {unprotectedCount > 0 ? `${unprotectedCount} unprotected` : 'fully protected'}
                </div>
              </button>
            );
          })}
        </div>
        {hasOverflow && (
          <button
            type='button'
            data-testid='entity-tiles-toggle'
            onClick={() => setExpanded((v) => !v)}
            className='sg-small mt-2'
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: BRAND.blue,
              fontWeight: 600,
            }}
          >
            {expanded
              ? 'Show fewer'
              : `Show ${orderedTiles.length - collapsedCount} more entity type${orderedTiles.length - collapsedCount === 1 ? '' : 's'}`}
          </button>
        )}
        {selectedTile && (
          <div className='mt-3' style={{ border: '1px solid var(--ink-150)', borderRadius: 'var(--r-md)', padding: 14 }}>
            <div className='d-flex align-items-center mb-2' style={{ gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: CATEGORY_COLORS[entityCategory(selectedTile.entityType)] || BRAND.blue,
                }}
              />
              <strong>{entityLabel(selectedTile.entityType)}</strong>
              <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
                {entityCategory(selectedTile.entityType)} · {selectedTile.sources.length} sources ·{' '}
                {formatCount(selectedTile.total)} instances
              </span>
            </div>
            <table className='w-100' style={{ fontSize: 13, textAlign: 'left' }}>
              <thead>
                <tr className='sg-eyebrow' style={{ fontSize: 11, color: 'var(--ink-400)' }}>
                  <th style={{ fontWeight: 600, padding: '4px 8px', textAlign: 'left' }}>WHERE FOUND</th>
                  <th style={{ fontWeight: 600, padding: '4px 8px', textAlign: 'left' }}>INSTANCES</th>
                  <th style={{ fontWeight: 600, padding: '4px 8px', textAlign: 'left' }}>PROTECTION</th>
                </tr>
              </thead>
              <tbody>
                {selectedTile.sources.map((source) => (
                  <tr key={source.dataSourceId} style={{ borderTop: '1px solid var(--ink-100)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <Link
                        to={`/landscape/riskprofile/${encodeURIComponent(source.dataSourceId)}`}
                        style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
                      >
                        {source.name}
                      </Link>
                      {(() => {
                        // Entity payload carries no origin — join from the loaded connections.
                        const conn = (connections || []).find((c) => String(c.id) === String(source.dataSourceId));
                        return (
                          <AdoptedFromBadge
                            registeredServiceId={conn?.registeredServiceId}
                            registeredServiceName={conn?.registeredServiceName}
                          />
                        );
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: FONT_MONO }}>{formatCount(source.instanceCount)}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <ProtectionPill source={source} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default EntityTiles;
export { protectionOf, isProtected };
